// Shared entity-id -> title resolution with a module-level cache, so every consumer
// (queue row titles, board cards, prerequisite chips) resolves each entity at most once
// per session and re-renders show titles instantly.

import { useEffect, useState } from 'react';
import { fetchEntity } from '../api/client.js';

const titleCache = new Map<string, string>(); // `${courseId}:${id}` -> title

export function cachedEntityTitle(courseId: string, entityId: number): string | undefined {
  return titleCache.get(`${courseId}:${entityId}`);
}

/**
 * Resolve entity ids to titles, fetching only the ids the cache is missing. Failed lookups
 * stay unresolved (empty string in the result) so callers fall back to showing the id.
 */
export async function resolveEntityTitles(courseId: string, ids: number[]): Promise<Record<number, string>> {
  const missing = ids.filter((id) => !titleCache.has(`${courseId}:${id}`));
  await Promise.all(
    missing.map(async (id) => {
      try {
        const entity = await fetchEntity(courseId, id);
        titleCache.set(`${courseId}:${id}`, entity.title ?? `Entity ${id}`);
      } catch {
        // Leave unresolved; callers fall back to the id.
      }
    }),
  );
  return Object.fromEntries(ids.map((id) => [id, titleCache.get(`${courseId}:${id}`) ?? ''])) as Record<
    number,
    string
  >;
}

/** Live title for one entity, or null while unresolved / when the lookup fails. */
export function useEntityTitle(courseId: string | undefined, entityId: number | null): string | null {
  const [title, setTitle] = useState<string | null>(() =>
    courseId && entityId != null ? (titleCache.get(`${courseId}:${entityId}`) ?? null) : null,
  );

  useEffect(() => {
    if (!courseId || entityId == null) {
      setTitle(null);
      return;
    }
    const cached = titleCache.get(`${courseId}:${entityId}`);
    if (cached) {
      setTitle(cached);
      return;
    }
    let cancelled = false;
    void resolveEntityTitles(courseId, [entityId]).then((titles) => {
      if (!cancelled) {
        setTitle(titles[entityId] || null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [courseId, entityId]);

  return title;
}
